import { MenuButtonGroup } from '@/components/features/SettingMenuButtonGroup';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useNavState } from '@/providers/NavStateProvider';
import { useEffect } from 'react';
import { useLoading } from '@/providers/LoadingProvider';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useUpdateTeam } from '@/hooks/api/0200_update_team';
import { toastSuccess } from '@/lib/toast';
import { useInvalidateTeams } from '@/hooks/api/0180_teams';
import { useCurrentTeam } from '@/providers/TeamProvider';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';

type FormInputs = {
  identifier: string;
  id: number;
  name: string;
  description: string;
  agency_enabled: boolean;
  sales_order_menu_listed: boolean;
  purchase_order_menu_listed: boolean;
};

export const WorkspaceTeamEdit = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  useEffect(() => setTitle('チーム基本設定編集'), []);
  useEffect(() => setBreadcrumbs([{ label: `チーム設定`, path: `/w/${_w_!.identifier}/setting/teams` }]), []);

  const navigate = useNavigate();
  const { team_id } = useParams();
  const { setShowLoading } = useLoading();
  const { _w_, me, _t_ } = useCurrentTeam();
  const { mutate, data, isPending, error } = useUpdateTeam();
  const { invalidate } = useInvalidateTeams(_w_!.identifier);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      id: Number(team_id!),
      name: _t_!.name,
      description: _t_!.description,
      agency_enabled: _t_!.agency_enabled,
      sales_order_menu_listed: _t_!.sales_order_menu_listed,
      purchase_order_menu_listed: _t_!.purchase_order_menu_listed,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      toastSuccess('チーム基本設定を編集しました');
      invalidate(); // Teamsのキャッシュクリア
      navigate(-1);
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  if (_t_ == null) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <MenuButtonGroup selected={'team'} />

        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">チーム基本設定編集</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0">
                  <ApiErrorMessages error={error} />

                  <div className="form-group">
                    <label>
                      チーム名<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={100}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'チーム名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </div>
                  <div className="form-group">
                    <label>メモ</label>
                    <textarea
                      rows={5}
                      className={`form-control ${errors.description && 'is-invalid'}`}
                      {...register('description')}
                    ></textarea>
                    {errors.description && <div className="text-xs text-danger mt-1">{errors.description.message}</div>}
                  </div>
                  <div className="form-group">
                    <label>プロジェクト（売上）に表示</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-sales-orders"
                        className="form-check-input"
                        {...register('sales_order_menu_listed')}
                      />
                      <label className="form-check-label" htmlFor="on-sales-orders">
                        メニューのプロジェクト（売上）に表示されます
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>プロジェクト（費用）に表示</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-purchase-orders"
                        className="form-check-input"
                        {...register('purchase_order_menu_listed')}
                      />
                      <label className="form-check-label" htmlFor="on-purchase-orders">
                        メニューのプロジェクト（費用）に表示されます
                      </label>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>代理店を有効化</label>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        id="on-agency_enabled"
                        className="form-check-input"
                        {...register('agency_enabled')}
                      />
                      <label className="form-check-label" htmlFor="on-agency_enabled">
                        チームのプロジェクト（受注）で代理店フィールドが有効になります。
                      </label>
                    </div>
                  </div>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button type="submit" className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>変更を確定する
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
